/**
 * excluded_custom 编辑器：targeting_id=`、`-joined 人群包 id，ext_info=`、`-joined
 * 名称（与 id 平行，id[i] ↔ name[i]）。配对行：每行一个 id（只读 + 删除）+ 名称输入；
 * 「添加」需填 id+名称。names 用 splitDyKeep/joinDyKeep 保留空段以维持平行索引。
 */
import { useState } from "react";
import type { EditorProps } from "./types";
import { joinDy, joinDyKeep, splitDy, splitDyKeep } from "../../utils/redlineHelpers";

interface Pair {
  id: string;
  name: string;
}

export function ExcludedCustomEditor({ item, onChange }: EditorProps) {
  const ids = splitDy(item.targeting_id);
  const names = splitDyKeep(item.ext_info);
  const pairs: Pair[] = ids.map((id, i) => ({ id, name: names[i] ?? "" }));

  const [idDraft, setIdDraft] = useState("");
  const [nameDraft, setNameDraft] = useState("");

  function setName(i: number, v: string) {
    const next = pairs.map((p) => p.name);
    next[i] = v;
    onChange({ targeting_id: item.targeting_id, ext_info: joinDyKeep(next) });
  }

  function remove(i: number) {
    const nextIds = pairs.map((p) => p.id);
    const nextNames = pairs.map((p) => p.name);
    nextIds.splice(i, 1);
    nextNames.splice(i, 1);
    onChange({ targeting_id: joinDy(nextIds), ext_info: joinDyKeep(nextNames) });
  }

  function add() {
    const id = idDraft.trim();
    if (!id) return;
    if (pairs.some((p) => p.id === id)) {
      setIdDraft("");
      return;
    }
    const nextIds = [...pairs.map((p) => p.id), id];
    const nextNames = [...pairs.map((p) => p.name), nameDraft.trim()];
    onChange({ targeting_id: joinDy(nextIds), ext_info: joinDyKeep(nextNames) });
    setIdDraft("");
    setNameDraft("");
  }

  return (
    <div className="space-y-2">
      {pairs.map((p, i) => (
        <div key={`${p.id}-${i}`} className="rl-subbox flex flex-wrap items-center gap-2">
          <span className="leo-tag is-info">
            {p.id}
            <button
              type="button"
              className="ml-1 text-text-muted hover:text-text-emphasis"
              onClick={() => remove(i)}
              aria-label={`移除 ${p.id}`}
            >
              ×
            </button>
          </span>
          <input
            type="text"
            className="leo-input min-w-[14rem] flex-1"
            value={p.name}
            onChange={(e) => setName(i, e.target.value)}
            placeholder="人群包名称（与 id 对应）"
          />
        </div>
      ))}
      <div className="flex flex-nowrap items-center gap-2 border-t border-line-subtle pt-2">
        <input
          type="text"
          className="leo-input min-w-0 flex-[1]"
          placeholder="新人群包 id"
          value={idDraft}
          onChange={(e) => setIdDraft(e.target.value)}
        />
        <input
          type="text"
          className="leo-input min-w-0 flex-[4]"
          placeholder="新人群包名称"
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className="leo-btn-ghost shrink-0" onClick={add}>
          添加
        </button>
      </div>
    </div>
  );
}
